import { DialogTitle, Grid, TextField, Tooltip, Typography } from '@mui/material';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import { cloneDeep } from 'lodash';
import React, { useEffect, useState } from 'react';
import Api from '../../Api';
import { IDispatchNote, IDispatchNoteDialogProps, IDispatchRequest } from '../../interfaces';



export const DispatchNoteDialog: React.SFC<IDispatchNoteDialogProps> = ({
    dispatchRequestId,
    dispatchRequestReference,
    disabled,
    ...props
}: IDispatchNoteDialogProps) => {

    const onGenerate = async (e: React.FormEvent) => {

        e.preventDefault();

        if (dispatchRequestId && dispatchNote) {

            setLoading(true);

            let generations = dispatchNote.sites.length;

            for (var i = 0; i < generations; i++) {

                let thisIssue = cloneDeep(dispatchNote);
                thisIssue.issue = i;

                await Api.Dispatches.dispatchnote(thisIssue)
                    .then((response: any) => response.blob())
                    .then((blob) => {

                        const noteReference = dispatchRequestReference.substring(3);

                        const filename = `${generations > 1 ? getIssueReference(i, noteReference) : noteReference
                            } - ${new Date().toUTCString()}.pdf`;

                        const url = window.URL.createObjectURL(
                            new Blob([blob]),
                        );

                        const link: HTMLAnchorElement = document.createElement('a');

                        link.href = url;
                        link.setAttribute('download',
                            filename,
                        );
                        document.body.appendChild(link);
                        link.click();
                        link.parentNode?.removeChild(link);

                        if ((i + 1) === generations) {
                            //last dispatch note is complete
                            setOpen(false);
                            setLoading(false);
                            setDispatchNote(null);
                        }
                    });
            }
        }
    }

    const getIssueReference = (issue: number, reference: string) => {

        if (issue < 0 || issue > 25)
            return reference;

        let position = 4;//0-based
        let issueChar = String.fromCharCode('A'.charCodeAt(0) + (issue));
        if (position > reference.length) {
            return reference;
        }

        let ref = reference.slice(0, position) + issueChar.toLocaleLowerCase() + reference.slice(position);
        return ref;
    }

    const onUpdate = (value: string, siteId: string | undefined) => {

        let updatedNote = cloneDeep(dispatchNote);
        if (updatedNote)
            if (!siteId) {
                updatedNote.notes = value;
            } else {
                updatedNote?.sites.forEach(site => {
                    if (site.siteId === siteId)
                        site.detail = value
                });
            }

        setDispatchNote(updatedNote);
    }

    const onCancel = () => {
        setOpen(false);
        setDispatchNote(null);
        setLoading(false);
    }


    const onOpenDialog = () => {

        setLoading(true);
        //get dispatch note stuff....
        if (dispatchRequestId)
            Api.Dispatches.get(dispatchRequestId).then((data: any) => {

                let d = data as IDispatchRequest;
                let dispatchNote: IDispatchNote = {
                    issue: 0,
                    dispatchRequestId,
                    notes: "",
                    sites: []
                }

                if (d.materials) {
                    d.materials.forEach(material => {
                        if (!dispatchNote.sites.some(site => site.siteId === material.site?.id)) {
                            //add the new site
                            dispatchNote.sites.push({
                                siteId: material.site?.id ?? "",
                                site: material.site?.value ?? "",
                                detail: ""
                            });
                        }
                    });
                }

                setDispatchNote(dispatchNote);
                setLoading(false);
                setOpen(true);
            });
    }

    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [dispatchNote, setDispatchNote] = useState<IDispatchNote | null>(null);

    useEffect(() => { }, [dispatchNote]);

    return (
        <>
            <Tooltip title="Download Dispatch Note">
                <Button
                    variant="outlined"
                    onClick={onOpenDialog}
                    disabled={loading === true || disabled === true}
                >
                    Dispatch Note
                </Button>
            </Tooltip>
            <Dialog
                open={open}
                onClose={() => setOpen(false)}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>
                    Download Dispatch Note(s)
                </DialogTitle>
                <form
                    onSubmit={onGenerate}
                >
                    <DialogContent>

                        {dispatchNote?.sites.map(site => {
                            return (
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2">{site.site}</Typography>
                                    <TextField
                                        key={site.siteId}
                                        label={"Number of Pallets on Delivery"}
                                        fullWidth
                                        required
                                        value={dispatchNote.sites.filter(x => x.siteId === site.siteId)[0].detail}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                            onUpdate(e.currentTarget.value, site.siteId)
                                        }
                                        sx={{ mb: 2 }}
                                    />
                                </Grid>
                            );
                        })}


                        <TextField
                            label="Notes"
                            sx={{ mb: 2 }}
                            multiline
                            fullWidth
                            rows={4}
                            value={dispatchNote?.notes}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                onUpdate(e.currentTarget.value, undefined)
                            }
                        />


                        {dispatchNote && dispatchNote.sites?.length > 1 &&
                            <Alert
                                color="info"
                            >
                                A dispatch note will be generated for each site dispatching material
                            </Alert>
                        }


                    </DialogContent>
                    <DialogActions>
                        <Button onClick={onCancel} color="warning">Cancel</Button>
                        <Button
                            disabled={loading}
                            type="submit"
                        >
                            Download
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>
        </>
    );
}